import React from 'react'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { AuditType } from './SectionSelection'
import { SectionIndicatorLegend } from './SectionIndicatorLegend'
import styles from './SectionDropdown.module.css'

interface SectionDropdownProps {
  value: string | null
  onChange: (sectionKey: string) => void
  sections: Array<{ key: string; title: string }>
  auditType: AuditType | null
  sectionPromptStatus?: Record<string, { hasPrompt: boolean; isEnabled: boolean }>
}

export default function SectionDropdown({
  value,
  onChange,
  sections,
  auditType,
  sectionPromptStatus = {},
}: SectionDropdownProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    onChange(newValue)
  }

  const getIndicatorClass = (sectionKey: string): string => {
    const status = sectionPromptStatus[sectionKey]

    // Three states:
    // Gray - No prompt exists
    // Yellow - Prompt exists but disabled
    // Green with check - Prompt exists and enabled
    if (!status || !status.hasPrompt) {
      return styles.indicatorNone
    }

    if (status.isEnabled) {
      return styles.indicatorEnabled
    }

    return styles.indicatorDisabled
  }

  const getIndicatorContent = (sectionKey: string): string => {
    const status = sectionPromptStatus[sectionKey]

    // No prompt - empty
    if (!status || !status.hasPrompt) {
      return ''
    }

    // Prompt exists and enabled - checkmark
    if (status.isEnabled) {
      return '✓'
    }

    // Prompt exists but disabled - X
    return '✗'
  }

  return (
    <div>
      <TextField
        label="Section"
        select
        fullWidth
        value={value || ''}
        onChange={handleChange}
        disabled={!auditType}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              style: {
                maxHeight: 400,
              },
            },
          },
        }}
      >
        <MenuItem value="" disabled>
          {auditType ? 'Select Section' : 'Select Audit Type First'}
        </MenuItem>
        {sections.map(({ key, title }) => (
          <MenuItem key={key} value={key}>
            <span className={styles.menuItemContent}>
              <span className={`${styles.statusIndicator} ${getIndicatorClass(key)}`}>
                {getIndicatorContent(key)}
              </span>
              {title}
            </span>
          </MenuItem>
        ))}
      </TextField>
      <SectionIndicatorLegend />
    </div>
  )
}
